import { useLocale } from '@mirai/locale';
import { Icon, Notification, styles, Text, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import { RoomCard } from './Booking.RoomCard';
import { useSelectedRoomData } from './hooks';
import { ICON } from '../../helpers';
import { L10N } from '../Booking.l10n';
import * as style from '../Booking.module.css';

const FormEditBoard = ({ dataSource = {}, responseBoard = {}, onChange = () => {}, ...others }) => {
  const { isDesktop } = useDevice();
  const { currencyFormat, translate } = useLocale();
  const { currency, details = [], modify: { board: { rooms = [] } = {} } = {}, price = {} } = dataSource;
  const { selectedRoomData: { image, occupation, plan, rate, title } = {}, showSelectedRoom } = useSelectedRoomData({
    details,
    room: responseBoard.room,
    rooms,
  });

  useEffect(() => {
    const { id: roomId, option: { boardId: option, price: { value: price } = {} } = {} } = responseBoard || {};

    onChange({ option, price, roomId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseBoard.id, responseBoard.option]);

  return (
    <View {...others} wide className={styles(showSelectedRoom && style.formEditBoard, others.className)}>
      {showSelectedRoom && (
        <RoomCard
          {...{ image, occupation, plan, rate, title }}
          className={style.selectedRoomCard}
          testId="booking-edit-board-room-card"
        />
      )}
      <View wide className={styles(style.summaryContainer, style.occupationComparison)}>
        <View className={styles(style.column, style.current)} testId="booking-current-board-summary">
          <Text bold headline={isDesktop}>
            {translate(L10N.LABEL_CURRENT_BOARD)}
          </Text>
          <Text action>{plan}</Text>
          <View wide>
            <Text bold>{translate(L10N.LABEL_TOTAL)}</Text>
            <Text bold headline>
              {currencyFormat({ currency: responseBoard.currency || currency, value: price.value })}
            </Text>
            <Text small>{translate(L10N.LABEL_INCLUDES_TAXES)}</Text>
          </View>
        </View>

        <View className={style.column} testId="booking-new-board-summary">
          <Text bold headline={isDesktop}>
            {translate(L10N.LABEL_NEW_BOARD)}
          </Text>
          <View row className={style.highlights}>
            <View row className={style.highlight}>
              <Icon success value={ICON.SUCCESS} />
              <Text bold success>
                {responseBoard.option?.name}
              </Text>
            </View>
          </View>
          <View wide>
            <Text bold>{translate(L10N.LABEL_TOTAL)}</Text>
            <Text bold headline>
              {currencyFormat({
                currency: responseBoard.currency || currency,
                value: responseBoard.option?.price?.value,
              })}
            </Text>
            <Text small>{translate(L10N.LABEL_INCLUDES_TAXES)}</Text>
          </View>
        </View>
        {responseBoard?.adjustDifferenceAtProperty && (
          <View className={style.adjustDifferent}>
            <Notification info wide>
              {translate(L10N.NOTIFICATION_ADJUST_DIFFERENT)}
            </Notification>
          </View>
        )}
      </View>
    </View>
  );
};

FormEditBoard.displayName = 'Mirai:Core:Booking:FormEditBoard';

FormEditBoard.propTypes = {
  dataSource: PropTypes.shape({
    currency: PropTypes.string,
    details: PropTypes.arrayOf(PropTypes.shape({})),
    modify: PropTypes.shape({
      board: PropTypes.shape({
        rooms: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            options: PropTypes.arrayOf(PropTypes.shape({})),
          }),
        ),
      }),
    }),
    price: PropTypes.shape({
      value: PropTypes.number,
    }),
  }),
  responseBoard: PropTypes.shape({
    adjustDifferenceAtProperty: PropTypes.bool,
    currency: PropTypes.string,
    id: PropTypes.string,
    option: PropTypes.shape({
      boardId: PropTypes.number,
      name: PropTypes.string,
      price: PropTypes.shape({
        value: PropTypes.number,
      }),
    }),
    room: PropTypes.shape({
      id: PropTypes.string,
      index: PropTypes.number,
    }),
  }),
  onChange: PropTypes.func,
};

export { FormEditBoard };
